import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Paper,
  alpha,
  Divider
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingIcon,
  Psychology as AIIcon,
  Group as PeopleIcon,
  Timeline as TimelineIcon,
  Article as NewsIcon,
  Assessment as InsightsIcon,
  Compare as CompareIcon,
  DataUsage as DataIcon,
  Speed as SpeedIcon,
  Lightbulb as LightbulbIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const Features = ({ onNavigate }) => {
  const features = [
    {
      icon: <AnalyticsIcon />,
      title: 'Real-time Analytics',
      description: 'Live election data, voter turnout tracking, and constituency performance metrics updated in real-time.',
      category: 'Analytics',
      color: '#1976d2'
    },
    {
      icon: <AIIcon />,
      title: 'AI-Powered Insights',
      description: 'Machine learning algorithms analyze voting patterns, predict outcomes, and provide intelligent recommendations.',
      category: 'AI/ML',
      color: '#7b1fa2'
    },
    {
      icon: <PeopleIcon />,
      title: 'Demographic Analysis',
      description: 'Comprehensive voter demographics including age, gender, caste, and socio-economic data analysis.',
      category: 'Demographics',
      color: '#388e3c'
    },
    {
      icon: <TimelineIcon />,
      title: 'Historical Trends',
      description: 'Multi-election comparison, trend analysis, and historical performance tracking across constituencies.',
      category: 'Trends',
      color: '#f57c00'
    },
    {
      icon: <NewsIcon />,
      title: 'News Integration',
      description: 'Real-time news aggregation with sentiment analysis and relevance scoring for each constituency.',
      category: 'Media',
      color: '#d32f2f'
    },
    {
      icon: <CompareIcon />,
      title: 'Comparative Analysis',
      description: 'Side-by-side constituency comparisons, benchmark analysis, and performance ranking systems.',
      category: 'Analysis',
      color: '#5d4037'
    },
    {
      icon: <DataIcon />,
      title: 'Data Visualization',
      description: 'Interactive charts, maps, and dashboards with customizable views and export capabilities.',
      category: 'Visualization',
      color: '#455a64'
    },
    {
      icon: <SpeedIcon />,
      title: 'Fast Performance',
      description: 'Optimized for speed with caching, efficient queries, and responsive design for all devices.',
      category: 'Performance',
      color: '#00695c'
    },
    {
      icon: <LightbulbIcon />,
      title: 'Strategic Insights',
      description: 'Campaign strategy recommendations, voter outreach optimization, and tactical intelligence.',
      category: 'Strategy',
      color: '#e65100'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              CivIQ Features
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Powerful Features for
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Political Intelligence
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mt: 3, opacity: 0.9 }}>
              Comprehensive suite of tools designed for political analysts, campaign managers, 
              researchers, and election observers
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(feature.color, 0.15)}`
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: alpha(feature.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      {React.cloneElement(feature.icon, { 
                        sx: { color: feature.color, fontSize: 24 } 
                      })}
                    </Box>
                    <Chip 
                      label={feature.category} 
                      size="small" 
                      sx={{ 
                        backgroundColor: alpha(feature.color, 0.1),
                        color: feature.color,
                        fontWeight: 600
                      }} 
                    />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Why Choose CivIQ?
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            The most comprehensive political analytics platform in India
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', border: '2px solid #e3f2fd' }}>
                <StarIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  650+
                </Typography>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Constituencies Covered
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete coverage of Lok Sabha constituencies with detailed analytics
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', border: '2px solid #e8f5e8' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#388e3c', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                  99.9%
                </Typography>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Data Accuracy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verified data from Election Commission and trusted sources
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', border: '2px solid #fff3e0' }}>
                <SpeedIcon sx={{ fontSize: 48, color: '#f57c00', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="warning.main" gutterBottom>
                  &lt;1s
                </Typography>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Query Response
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lightning-fast analytics with optimized database queries
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join political analysts and researchers using CivIQ for data-driven insights
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                onClick={() => onNavigate('login')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Start Free Trial
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => onNavigate('pricing')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                View Pricing
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  {/* <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} /> */}
                  <Box
                                component="img"
                                src={civiqLogo}
                                alt="CivIQ Logo"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  mr: 1,
                                  border: '1px solid rgba(255,255,255,0.3)'
                                }}
                              />
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  CivIQ
                </Typography>
              </Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Platform
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('features')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Features
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('pricing')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Pricing
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('api')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      API
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Company
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('about')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      About
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('contact')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Contact
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('privacy')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Privacy Policy
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Box textAlign="center">
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2025 CivIQ. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Features;
